import Button from "@components/ui/button";
import {
  Card,
  CardCloseButton,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useRef, useState } from "react";
import { CsvFileIcon } from "./csv-file-icon";
import {
  cellAt,
  columnLetter,
  gridColumnCount,
  gridRowCount,
  setCell,
  toMatrix,
  trimMatrix,
  type DataMatrix,
} from "./data-grid";
import { readMatrixCsvFile } from "./matrix-csv";

const ID = "add-data-dialog";

interface Props {
  /** Current value of the data variable (matrix, or the legacy record shape). */
  value: unknown;
  /** Commits the trimmed matrix; `fileName` is set when it came from a CSV. */
  onSubmit: (matrix: DataMatrix, fileName?: string) => void;
}

/**
 * Spreadsheet editor for a `data` variable (Figma 642:51593).
 *
 * The whole table is populated from this matrix, so cells are positional —
 * column letters are addresses, exactly like a spreadsheet, and there are no
 * `[Key]` placeholders to match (that is the list variable's mechanism).
 */
export const AddDataDialog = ({ value, onSubmit }: Props) => {
  const dispatch = useAppDispatch();
  const [matrix, setMatrix] = useState<DataMatrix>(() => toMatrix(value));
  const [fileName, setFileName] = useState<string | undefined>();
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const close = () => dispatch(closeDialog(ID));

  const columnCount = gridColumnCount(matrix);
  const rowCount = gridRowCount(matrix);
  const trimmed = trimMatrix(matrix);
  const canSubmit = trimmed.length > 0;

  const selectFile = async (file: File | undefined) => {
    if (!file) return;
    setError("");
    try {
      // Lenient MATRIX parse: this sheet is positional, so empty/duplicate
      // header cells, blank rows and ragged rows are all valid content.
      setMatrix(await readMatrixCsvFile(file));
      setFileName(file.name);
    } catch (exception) {
      setError(
        exception instanceof Error ? exception.message : "Could not read CSV",
      );
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(trimmed, fileName);
    close();
  };

  return (
    <Card className="tw-w-[792px]">
      <CardHeader className="tw-flex tw-items-center tw-justify-between">
        <CardTitle>Add data</CardTitle>
        <div className="tw-flex tw-items-center tw-gap-4">
          <input
            ref={inputRef}
            className="tw-hidden"
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => void selectFile(event.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="tw-bg-transparent tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-none tw-p-0 tw-font-sans tw-text-sm tw-font-normal tw-text-blue"
          >
            <CsvFileIcon className="tw-size-4" />
            Upload CSV
          </button>
          <CardCloseButton onClick={close} />
        </div>
      </CardHeader>

      <CardContent className="tw-w-auto tw-p-0">
        {error && (
          <p
            role="alert"
            className="tw-m-0 tw-px-6 tw-pt-4 tw-text-xs tw-text-[#D43548]"
          >
            {error}
          </p>
        )}
        <div className="tw-max-h-[420px] tw-overflow-auto">
          <table className="tw-w-full tw-border-collapse">
            <thead>
              <tr>
                <th className="tw-sticky tw-left-0 tw-top-0 tw-z-10 tw-w-12 tw-min-w-[48px] tw-border tw-border-solid tw-border-blue-100 tw-bg-blue-50" />
                {Array.from({ length: columnCount }, (_, column) => (
                  <th
                    key={column}
                    scope="col"
                    className="tw-sticky tw-top-0 tw-border tw-border-solid tw-border-blue-100 tw-bg-blue-50 tw-px-2 tw-py-2 tw-font-sans tw-text-sm tw-font-normal tw-text-blue"
                  >
                    {columnLetter(column)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rowCount }, (_, row) => (
                <tr key={row}>
                  <th
                    scope="row"
                    className="tw-sticky tw-left-0 tw-w-12 tw-min-w-[48px] tw-border tw-border-solid tw-border-blue-100 tw-bg-blue-50 tw-px-1 tw-text-center tw-font-sans tw-text-sm tw-font-normal tw-text-blue-500"
                  >
                    {/* Row numbers only label rows that hold data — matches the
                        design, where the trailing blank rows are unnumbered. */}
                    {row < matrix.length ? row + 1 : ""}
                  </th>
                  {Array.from({ length: columnCount }, (_, column) => (
                    <td
                      key={column}
                      className="tw-border tw-border-solid tw-border-blue-100 tw-p-0"
                    >
                      <input
                        aria-label={`${columnLetter(column)}${row + 1}`}
                        value={cellAt(matrix, row, column)}
                        onChange={(event) =>
                          setMatrix((current) =>
                            setCell(current, row, column, event.target.value),
                          )
                        }
                        className={cn(
                          "tw-h-10 tw-w-full tw-min-w-[110px] tw-border-none tw-bg-white tw-px-3",
                          "tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-outline-none",
                          "focus:tw-bg-[#f2f0ff]",
                        )}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>

      <CardFooter className="tw-justify-end tw-gap-4">
        <Button variant="secondary" onClick={close}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={!canSubmit}>
          Add data
        </Button>
      </CardFooter>
    </Card>
  );
};

AddDataDialog.id = ID;
